import React, {useCallback, useEffect, useRef, useState} from "react";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {BizFormAnswerForm} from "../../../../../../packages/biz-form";
import {useBizFormApiResolver} from "../../../../../hooks/use-biz-form-api-resolver";
import {useBizFileUrlsResolver} from "../../../../../hooks/use-biz-file-urls-resolver";
import LoadingLottieAnimation from "../../../../app-specific/lottie-animations/loading";
import {Box, Divider} from "@mui/material";
import Button from "../../../../base/button/button";

const AddNCRQuestionsSection = ({
                                    formId: formIdProps,
                                    initialRequest,
                                    newResponse,
                                    formResponseId,
                                    submitting,
                                    submit,
                                    goBack,
                                }) => {
    const ncrBizFormApi = useBizFormApiResolver((t) => t.ncrService);
    const ncrBizFile = useBizFileUrlsResolver((t) => t.ncrBizForm);
    const [hasErrors, setHasErrors] = useState(false);
    const [loadingInitialStatus, setLoadingInitialStatus] = useState(false);
    const [submittingForm, setSubmittingForm] = useState(false);
    const [formId, setFormId] = useState(formIdProps ?? "");
    const isMounted = useIsMounted();
    const submitFuncRef = useRef(submit);
    submitFuncRef.current = submit;

    /**@type {React.MutableRefObject<BizFormAnswerFormRef>}*/
    const ref = useRef(null);

    /**
     * Fetches the initial statusId of NCR reports.
     * @type {function(): Promise<number>}
     */
    const getInitialStatusFormReference = useCallback(async () => {
        setLoadingInitialStatus(true)
        const response = await Api.getNcrInitialStatus();
        if (!isMounted())
            return;
        setLoadingInitialStatus(false);
        if (!response.data?.id || !response.data?.formReference) {
            submitFuncRef.current?.()
            return;
        }
        setFormId(response.data.formReference);
    }, [isMounted])

    /**
     * As soon as the component mounts:
     * - Fetches the questions of the given statusId
     */
    useEffect(() => {
        if (!formIdProps && initialRequest) {
            (void getInitialStatusFormReference())
            return;
        }
        if (!formIdProps && !initialRequest) {
            submitFuncRef.current?.()
        }
    }, [formIdProps, getInitialStatusFormReference, initialRequest]);

    /**
     * Submits the form
     * @type {(function(): Promise<void>)|*}
     */
    const submitForm = useCallback(async () => {
        setSubmittingForm(true)
        await ref.current?.submit(newResponse)
        if (!isMounted())
            return;
        setSubmittingForm(false)
    }, [isMounted, newResponse])

    return (
        <>

            <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: -4}}>
                {
                    !loadingInitialStatus && formId &&
                    <BizFormAnswerForm
                        fileInfo={ncrBizFile.fileInfo}
                        apiClient={ncrBizFormApi}
                        ref={ref}
                        formId={formId}
                        formResponseId={formResponseId}
                        onSubmitted={submit}
                        newResponse={newResponse}
                        showResetButton={!newResponse}
                        showClearButton
                        setHasErrors={setHasErrors}
                    />
                }
                {
                    (loadingInitialStatus || (submitting && !formId)) &&
                    <>
                        <LoadingLottieAnimation
                            width={'80%'}
                            className={'my-5'}
                        />
                    </>
                }
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: 'background.paper',
                    width: '100%',
                    zIndex: 100,
                }}>
                    <Divider className={"divider"} flexItem/>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                        {
                            goBack &&
                            <Button
                                disabled={submitting || submittingForm}
                                variant={"dialog"}
                                color={"primary"}
                                type={'button'}
                                onClick={goBack}
                                borderRadius={"0 10px 0 10px"}
                            >
                                {submitting ? "submitting..." : "Back"}
                            </Button>
                        }
                        {
                            !goBack &&
                            <div/>
                        }
                        <Button
                            disabled={submitting || submittingForm || hasErrors}
                            variant={"dialog"}
                            color={"primary"}
                            onClick={submitForm}
                            borderRadius={"10px 0 10px 0"}
                        >
                            {
                                submitting
                                    ? "submitting..."
                                    : submittingForm
                                        ? 'Submitting Form'
                                        : newResponse ? "Next" : "Update"
                            }
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default AddNCRQuestionsSection;
